/**
 * Kiosk kapısı (#131): parola geçilene kadar kioskun hiçbir parçası kurulmaz — kamera
 * açılmaz, koleksiyon çekilmez, üretim yolu görünmez.
 *
 * Parola **sunucuda** doğrulanır; burada tutulan tek şey "kapı açık mı" bilgisidir. Erişim
 * çerezi HttpOnly'dir, bu kod onu okuyamaz. Süre dolduğunda ya da sunucu erişimi reddettiğinde
 * sayfa yenilenir: kapı yeniden sorulur ve yarım kalmış misafir oturumu da temizlenir.
 */

import { useCallback, useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import {
  fetchKioskSession,
  setUnauthorizedHandler,
  unlockKiosk,
  type KioskSession,
} from './api/client'
import { C, GlowBtn, Stars } from './ui'

const RETRY_MS = 2000

type Phase = 'checking' | 'locked' | 'open'

export default function KioskLock({ children }: { children: ReactNode }) {
  const [phase, setPhase] = useState<Phase>('checking')
  const [expiresIn, setExpiresIn] = useState<number | null>(null)
  const [password, setPassword] = useState('')
  const [wrong, setWrong] = useState(false)
  const [busy, setBusy] = useState(false)
  const [focused, setFocused] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const apply = useCallback((session: KioskSession) => {
    const open = !session.required || session.unlocked
    setPhase(open ? 'open' : 'locked')
    setExpiresIn(open && session.required ? session.expiresInSeconds : null)
  }, [])

  useEffect(() => {
    let cancelled = false
    let retry: number | undefined
    const load = async () => {
      try {
        const session = await fetchKioskSession()
        if (!cancelled) apply(session)
      } catch {
        // Sunucuya ulaşılamadı: kapıyı açmak da kilitlemek de yanlış olur — operatöre
        // yazamayacağı bir parola ekranı gösterme, bağlantı gelene kadar bekle
        if (!cancelled) retry = window.setTimeout(() => void load(), RETRY_MS)
      }
    }
    void load()
    return () => {
      cancelled = true
      window.clearTimeout(retry)
    }
  }, [apply])

  // Süre dolunca sayfa yenilenir; kapı yeniden sorulur
  useEffect(() => {
    if (expiresIn === null) return
    const timer = window.setTimeout(() => window.location.reload(), expiresIn * 1000)
    return () => window.clearTimeout(timer)
  }, [expiresIn])

  // Çerez süresi akış ortasında dolarsa ilk 401 kapıyı geri getirir
  useEffect(() => {
    setUnauthorizedHandler(() => window.location.reload())
    return () => setUnauthorizedHandler(null)
  }, [])

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    if (busy || !password) return
    setBusy(true)
    setWrong(false)
    try {
      if (await unlockKiosk(password)) {
        apply(await fetchKioskSession())
      } else {
        setWrong(true)
      }
    } catch {
      setWrong(true)
    } finally {
      setPassword('')
      setRevealed(false)
      setBusy(false)
      // Alan boşalınca GİR disabled oluyor ve tarayıcı odağı bırakıyor; tablette bu, klavyenin
      // kapanması demek. Yeniden denemek için operatör alana ikinci kez dokunmak zorunda kalmasın.
      inputRef.current?.focus()
    }
  }

  if (phase === 'open') return <>{children}</>

  return (
    <div
      style={{
        width: '100vw',
        height: '100dvh',
        background: C.deepBlack,
        fontFamily: "'Exo 2', sans-serif",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Stars count={70} />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 50% 0%, ${C.neonPurple}1A 0%, transparent 65%)`,
          pointerEvents: 'none',
        }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          width: 'min(380px, 86vw)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '26px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '12px',
              background: `linear-gradient(135deg, ${C.primaryPurple}, ${C.neonPurple})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 0 18px ${C.neonPurple}66`,
              fontSize: '20px',
            }}
          >
            ⚡
          </div>
          <div
            style={{
              fontWeight: 900,
              fontSize: '22px',
              color: C.white,
              letterSpacing: '0.12em',
            }}
          >
            CHAR<span style={{ color: C.neonPurple }}>GEN</span>
          </div>
        </div>

        {phase === 'checking' ? (
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '10px',
              letterSpacing: '0.22em',
              color: C.secText,
              animation: 'glow-pulse 1.4s ease-in-out infinite',
            }}
          >
            BAĞLANIYOR
          </div>
        ) : (
          <form
            onSubmit={(e) => void submit(e)}
            style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '14px' }}
          >
            <label
              htmlFor="kiosk-parola"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '9px',
                fontWeight: 700,
                color: C.secText,
                letterSpacing: '0.22em',
              }}
            >
              PAROLA
            </label>
            <div style={{ position: 'relative' }}>
              <input
                id="kiosk-parola"
                ref={inputRef}
                type={revealed ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                autoComplete="off"
                autoFocus
                enterKeyHint="go"
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  background: 'rgba(12,6,26,0.85)',
                  border: `1.5px solid ${
                    wrong ? C.magenta : focused ? C.neonPurple : 'rgba(139,92,255,0.2)'
                  }`,
                  borderRadius: '12px',
                  padding: '16px 52px 16px 18px',
                  color: C.white,
                  fontSize: '18px',
                  fontFamily: "'Exo 2', sans-serif",
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  outline: 'none',
                  caretColor: C.neonPurple,
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
                  boxShadow: wrong
                    ? `0 0 0 3px ${C.magenta}22`
                    : focused
                      ? `0 0 0 3px ${C.neonPurple}22, 0 0 20px ${C.neonPurple}22`
                      : 'none',
                }}
              />
              <button
                type="button"
                onClick={() => setRevealed((r) => !r)}
                // Odağı input'tan koparmasın -- tablette klavye kapanıp operatör ikinci kez
                // dokunmak zorunda kalmasın (aynı gerekçe submit'in odağı geri vermesiyle).
                onMouseDown={(e) => e.preventDefault()}
                aria-label={revealed ? 'Parolayı gizle' : 'Parolayı göster'}
                aria-pressed={revealed}
                style={{
                  position: 'absolute',
                  right: '6px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  color: C.secText,
                  cursor: 'pointer',
                  fontSize: '18px',
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                {revealed ? '🙈' : '👁️'}
              </button>
            </div>
            <GlowBtn size="lg" fullWidth disabled={busy || !password}>
              {busy ? '…' : 'GİR'}
            </GlowBtn>
            <div
              role="alert"
              style={{
                minHeight: '16px',
                textAlign: 'center',
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '10px',
                letterSpacing: '0.14em',
                color: C.magenta,
              }}
            >
              {wrong ? 'PAROLA HATALI' : ''}
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
